import Dexie from "dexie";
import Store from "@/prototype/configuration/Store.js";
import ModuleConfiguration from "@/prototype/configuration/ModuleConfiguration.js";
import ObjectConfiguration from "@/prototype/configuration/ObjectConfiguration.js";
import global from "@/prototype/global.js";

const log = global.log.getLogger("f_mtac.configuration");
log.setLevel(log.levels.WARN);

const MASTER_DATABASE_VERSION = 1;

const rights = {
    PROFILE_RIGHTS_NONE: 0,
    PROFILE_RIGHTS_READ: 1 << 0,
    PROFILE_RIGHTS_WRITE: 1 << 1,
    PROFILE_RIGHTS_READ_WRITE: (1 << 0) | (1 << 1),
    PROFILE_RIGHTS_SPECIAL: 1 << 2
};
const profiles = [
    {
        rights: rights.PROFILE_RIGHTS_NONE
    },
    {
        rights: rights.PROFILE_RIGHTS_READ
    },
    {
        rights: rights.PROFILE_RIGHTS_READ | rights.PROFILE_RIGHTS_WRITE
    },
    {
        rights: rights.PROFILE_RIGHTS_READ | rights.PROFILE_RIGHTS_WRITE | rights.PROFILE_RIGHTS_SPECIAL
    }
];
profiles.forEach((profile, level) => {
    profile.level = level;
});

/**
 * In charge of indexedDB f_mtac.master content
 */
const Configuration = function () {
    var master = new Dexie("f_mtac.master", {autoOpen: false});
    master.version(MASTER_DATABASE_VERSION).stores({
        stores: "name"
    });
    var stores = {};
    this.rights = rights;
    this.profiles = profiles;
    /*
    let store_delete_hook =  async (primary_key, store_data, transaction) => {
        log.error("store deleted", {primary_key, store_data, transaction});
    };
    */
    let store_create_hook = async (_, store_data) => {
        setTimeout(() => {
            global.event_bus.publish(`configuration.store.created.${store_data.name}`, {store: stores[store_data.name], store_data});
        }, 0);
    };
    let store_update_hook = async (modifications, _, store_data) => {
        setTimeout(() => {
            global.event_bus.publish(`configuration.store.updated.${store_data.name}`, {store: stores[store_data.name], modifications});
        }, 0);
    };

    var private_interface = {
        master,
        index: (name, store) => {
            stores[name] = store;
            this[name] = store.export();
        },
        deindex: (name) => {
            delete this[name];
            delete stores[name];
        }
    };

    this.init = async function () {
        await master.open();
        master.stores.each(({name, data}) => {
            log.debug("store ",{name, data});
            let store = new Store(name, data, private_interface);
            store.loaded();
        });
        // master.stores.hook("deleting", store_delete_hook);
        master.stores.hook("creating", store_create_hook);
        master.stores.hook("updating", store_update_hook);
    };
    this.remove_store = async function (name) {
        if (!(name in stores)) {
            return;
        }
        let store = stores[name];
        delete this[name];
        delete stores[name];
        store.delete();
    };
    this.add_store = async function (name, default_data) {
        let store = stores[name];
        if (name in stores) {
            return store;
        }
        store = new Store(name, default_data, private_interface);
        await store.save();
        return store;
    };
    this.store_on = function (store_name, event_name, handler) {
        switch (event_name) {
            case "update":
                global.event_bus.subscribe(`configuration.store.updated.${store_name}`, handler);
                break;
            case "create":
                global.event_bus.subscribe(`configuration.store.created.${store_name}`, handler);
                break;
        }
    };
    this.commit_store = async function (name) {
        await stores[name].save();
    };
    this.commit = async function () {
        let store_updates = [];
        for (let name in stores) {
            store_updates.push(stores[name].save());
        }
        await Promise.all(store_updates);
    };
    this.module_configuration = (name, computed_configuration) => new ModuleConfiguration(name, computed_configuration);
    this.object_configuration = (name, computed_configuration) => new ObjectConfiguration(name, computed_configuration);

    // Get the profile text for a given prfile number (=level)
    this.getProfileName = (level) => {
        switch (level) {
            case 1:
                return global.internationalization.text("user.observer");
            case 2:
                return global.internationalization.text("user.standard");
            case 3:
                return global.internationalization.text("user.expert");
            default:
                return global.internationalization.text("global.none");
        }
    };
};

global.app.bootstrap.register_module({
    name: "default_configuration",
    deps: ["app", "commissioning"],
    init: async () => {
        var server_default_configuration_json = await fetch(global.commissioning.uri.config, {cache: "reload"}).then(response => {return response.json();})
            .catch((error) => log.error(error));
        global.default_configuration = {};
        for (let name in server_default_configuration_json) {
            log.info("loaded default configuration for", name);
            global.default_configuration[name] = server_default_configuration_json[name];
        }
    }
});

global.app.bootstrap.register_module({
    name: "configuration",
    deps: ["app"],
    init: async () => {
        global.configuration = new Configuration();
        await global.configuration.init();
    }
});

export default Configuration;
